'use client';

import Link from 'next/link';
import Image from 'next/image';
import { siteConfig } from '@/config/site';
import { useTranslation } from '@/context/LanguageContext';
import { useSettings } from '@/context/SettingsContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faMapMarkerAlt,
  faPhoneAlt,
  faEnvelope,
  faClock,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import {
  faFacebookF,
  faInstagram,
  faTwitter,
  faYoutube,
  faLinkedinIn
} from '@fortawesome/free-brands-svg-icons';

export default function Footer() {
  const { t, language } = useTranslation();
  const { getSettingContent } = useSettings();
  const currentYear = new Date().getFullYear();
  const siteName = siteConfig.name;
  const siteNameEnglish = siteConfig.nameEnglish || siteConfig.name;
  const currentSiteName = language === 'en' ? siteNameEnglish : siteName;

  // Split site name for styling (e.g., "Universitas Mulawarman" -> ["Universitas", "Mulawarman"])
  const nameParts = currentSiteName.split(' ');
  const firstName = nameParts[0] || '';
  const restName = nameParts.slice(1).join(' ');

  // Get dynamic settings with fallbacks
  const facebookUrl = getSettingContent('social_facebook', siteConfig.socials.facebook);
  const instagramUrl = getSettingContent('social_instagram', siteConfig.socials.instagram);
  const twitterUrl = getSettingContent('social_twitter', siteConfig.socials.twitter);
  const youtubeUrl = getSettingContent('social_youtube', siteConfig.socials.youtube);
  const linkedinUrl = getSettingContent('social_linkedin', siteConfig.socials.linkedin);

  const contactAddress = getSettingContent('location_address', siteConfig.contact.address);
  const contactPhone = getSettingContent('social_phone', siteConfig.contact.phone);
  const contactEmail = getSettingContent('social_email', siteConfig.contact.email);
  const contactHours = getSettingContent('working_hours', siteConfig.contact.workingHours);

  const footerWords = language === 'id'
    ? getSettingContent('footer_words_idn', t('footer.about', { siteName: currentSiteName }))
    : getSettingContent('footer_words_en', t('footer.about', { siteName: currentSiteName }));

  return (
    <footer className="bg-unmulDarkGreen text-white">
      {/* Main Footer */}
      <div className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Logo & About */}
            <div className="lg:col-span-2">
              <Link href="/" className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center p-2">
                  <Image src={siteConfig.logo} alt={`Logo ${currentSiteName}`} width={48} height={48} className="w-full h-full object-contain" />
                </div>
                <div>
                  <span className="font-bold text-2xl font-serif">{firstName.toUpperCase()}</span>
                  {restName && <span className="font-bold text-2xl font-serif text-unmulYellow"> {restName.toUpperCase()}</span>}
                </div>
              </Link>
              <p className="text-gray-200 mb-6 max-w-md leading-relaxed">
                {footerWords}
              </p>
              <div className="flex gap-3">
                {/* Facebook */}
                {facebookUrl && (
                  <a href={facebookUrl} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 bg-white/10 hover:bg-unmulYellow hover:text-unmulDarkGreen rounded-full flex items-center justify-center transition">
                    <FontAwesomeIcon icon={faFacebookF} />
                  </a>
                )}
                {/* Instagram */}
                {instagramUrl && (
                  <a href={instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 bg-white/10 hover:bg-unmulYellow hover:text-unmulDarkGreen rounded-full flex items-center justify-center transition">
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                )}
                {/* Twitter */}
                {twitterUrl && (
                  <a href={twitterUrl} target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="w-10 h-10 bg-white/10 hover:bg-unmulYellow hover:text-unmulDarkGreen rounded-full flex items-center justify-center transition">
                    <FontAwesomeIcon icon={faTwitter} />
                  </a>
                )}
                {/* YouTube */}
                {youtubeUrl && (
                  <a href={youtubeUrl} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="w-10 h-10 bg-white/10 hover:bg-unmulYellow hover:text-unmulDarkGreen rounded-full flex items-center justify-center transition">
                    <FontAwesomeIcon icon={faYoutube} />
                  </a>
                )}
                {/* LinkedIn */}
                {linkedinUrl && (
                  <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 bg-white/10 hover:bg-unmulYellow hover:text-unmulDarkGreen rounded-full flex items-center justify-center transition">
                    <FontAwesomeIcon icon={faLinkedinIn} />
                  </a>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-bold text-lg mb-6 text-unmulYellow">{t('footer.quickLinks')}</h3>
              <ul className="space-y-3 text-gray-200">
                <li><Link href="/profil/sejarah-visi-misi" className="hover:text-unmulYellow transition flex items-center gap-2"><FontAwesomeIcon icon={faChevronRight} className="text-xs text-unmulYellow" /> {t('footer.aboutUs')}</Link></li>
                <li><Link href="/akademik" className="hover:text-unmulYellow transition flex items-center gap-2"><FontAwesomeIcon icon={faChevronRight} className="text-xs text-unmulYellow" /> {t('footer.academic')}</Link></li>
                <li><Link href="/pendaftaran" className="hover:text-unmulYellow transition flex items-center gap-2"><FontAwesomeIcon icon={faChevronRight} className="text-xs text-unmulYellow" /> {t('footer.registration')}</Link></li>
                <li><Link href="/riset" className="hover:text-unmulYellow transition flex items-center gap-2"><FontAwesomeIcon icon={faChevronRight} className="text-xs text-unmulYellow" /> {t('footer.researchInnovation')}</Link></li>
                <li><Link href="/kemahasiswaan" className="hover:text-unmulYellow transition flex items-center gap-2"><FontAwesomeIcon icon={faChevronRight} className="text-xs text-unmulYellow" /> {t('footer.studentAffairs')}</Link></li>
                <li><Link href="/kerjasama" className="hover:text-unmulYellow transition flex items-center gap-2"><FontAwesomeIcon icon={faChevronRight} className="text-xs text-unmulYellow" /> {t('footer.cooperation')}</Link></li>
                <li><Link href="/kontak" className="hover:text-unmulYellow transition flex items-center gap-2"><FontAwesomeIcon icon={faChevronRight} className="text-xs text-unmulYellow" /> {t('footer.contactUs')}</Link></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="font-bold text-lg mb-6 text-unmulYellow">{t('footer.contact')}</h3>
              <ul className="space-y-4 text-gray-200">
                <li className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-unmulYellow mt-1" />
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(currentSiteName)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-unmulYellow transition"
                  >
                    {contactAddress}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faPhoneAlt} className="text-unmulYellow" />
                  <a href={`tel:${contactPhone?.replace(/[^\d+]/g, '')}`} className="hover:text-unmulYellow transition">{contactPhone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faEnvelope} className="text-unmulYellow" />
                  <a href={`mailto:${contactEmail}`} className="hover:text-unmulYellow transition">{contactEmail}</a>
                </li>
                {contactHours && (
                  <li className="flex items-center gap-3">
                    <FontAwesomeIcon icon={faClock} className="text-unmulYellow" />
                    <span>{contactHours}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter */}
      {/* <div className="border-t border-white/10 py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-bold text-lg">{t('footer.newsletter')}</h3>
              <p className="text-gray-300 text-sm">{t('footer.newsletterText')}</p>
            </div>
            <div className="flex flex-col sm:flex-row w-full md:w-auto gap-2">
              <input
                type="email"
                placeholder={t('footer.emailPlaceholder')}
                className="w-full sm:w-auto flex-1 md:w-64 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-unmulYellow"
              />
              <button className="w-full sm:w-auto px-6 py-3 bg-unmulYellow text-unmulDarkGreen font-bold rounded-lg hover:bg-white transition">
                Submit <FontAwesomeIcon icon={faPaperPlane} />
              </button>
            </div>
          </div>
        </div>
      </div> */}

      {/* Copyright */}
      <div className="border-t border-white/10 py-6">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-300 text-sm">
              © {currentYear} {currentSiteName}. {t('common.allRightsReserved')}
            </p>
            <div className="flex gap-6 text-sm text-gray-300">
              <a href="#" className="hover:text-unmulYellow transition">{t('footer.privacyPolicy')}</a>
              <a href="#" className="hover:text-unmulYellow transition">{t('footer.terms')}</a>
              <a href="#" className="hover:text-unmulYellow transition">{t('footer.sitemap')}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
